import cloneDeep from 'lodash/cloneDeep';
import { Action, Dispatch } from 'redux';
import { IAppState } from './reducers';
import { queryJobs } from './jobStatus';
import { VAULT_VIDEO_TYPE } from '../constants/vault';

export enum VaultUploadStatus {
  NotStarted = 'NotStarted',
  Initializing = 'Initializing',
  CheckTranscode = 'CheckTranscode',
  PreProcessing = 'PreProcessing',
  Uploading = 'Uploading',
  PostProcessing = 'PostProcessing',
  Complete = 'Complete',
  Error = 'Error',
  Canceled = 'Canceled',
}

export interface IVaultUploadMetaDetails {
  description?: string;
}

export interface IVaultFile {
  uploadFile: File;
  uploadTrackingId: string;
  uploadStatus: VaultUploadStatus;
  progressPercentage?: string;
}

export interface IState {
  isOpen: boolean;
  isUploading: boolean;
  isUploadComplete: boolean;
  selectedFiles: Array<IVaultFile>;
  vaultAssetMetaDetails: IVaultUploadMetaDetails;
}

export enum ActionType {
  Toggle = 'VaultUpload.Toggle',
  Reset = 'VaultUpload.Reset',
  UpdateFiles = 'VaultUpload.UpdateFiles',
  UpdateDescription = 'VaultUpload.UpdateDescription',
  SetIsUploading = 'VaultUpload.SetIsUploading',
  SetIsUploadComplete = 'VaultUpload.SetIsUploadComplete',
}

export interface IVaultUploadAction extends Action<ActionType> {
  payload?: any;
}

const INITIAL_STATE: IState = {
  isOpen: false,
  isUploading: false,
  isUploadComplete: false,
  selectedFiles: [],
  vaultAssetMetaDetails: {},
};

export default function reducer(state: IState = INITIAL_STATE, action: IVaultUploadAction): IState {
  switch (action.type) {
    case ActionType.Toggle:
      return {
        ...state,
        isOpen: !state.isOpen,
      };
    case ActionType.Reset:
      return {
        ...INITIAL_STATE,
      };
    case ActionType.UpdateFiles:
      return {
        ...state,
        selectedFiles: action.payload,
      };
    case ActionType.SetIsUploading:
      return {
        ...state,
        isUploading: action.payload,
      };
    case ActionType.SetIsUploadComplete:
      return {
        ...state,
        isUploadComplete: action.payload,
      };
    case ActionType.UpdateDescription:
      return {
        ...state,
        vaultAssetMetaDetails: {
          ...state.vaultAssetMetaDetails,
          description: action.payload,
        },
      };
    default:
      return state;
  }
}

export const toggleVaultUploadDialog = () => ({
  type: ActionType.Toggle,
});

export const updateVaultUploadFiles = (selectedFiles: Array<IVaultFile>) => ({
  type: ActionType.UpdateFiles,
  payload: selectedFiles,
});

export const resetVaultUploadDialog = () => ({
  type: ActionType.Reset,
});

export const setIsUploading = (isUploading: boolean, isUploadComplete: boolean = false) => async (dispatch: Dispatch<any>, getState: () => IAppState) => {
  dispatch({
    type: ActionType.SetIsUploading,
    payload: isUploading,
  });
  const state = getState();
  await queryJobs([VAULT_VIDEO_TYPE.Clip, 'clips', VAULT_VIDEO_TYPE.MyVault, VAULT_VIDEO_TYPE.Vault])(dispatch, getState);

  if (isUploadComplete) {
    dispatch({
      type: ActionType.SetIsUploadComplete,
      payload: true,
    });
  }
};

export const updateDescriptionField = (value: string) => ({
  type: ActionType.UpdateDescription,
  payload: value,
});

export const setFileUploadStatus = (uploadTrackingId: string, uploadStatus: VaultUploadStatus) => async (dispatch: Dispatch<any>, getState: () => IAppState) => {
  const { selectedFiles } = getState().vaultUploadDialog;
  if (uploadTrackingId && selectedFiles && selectedFiles.length > 0) {
    const modifiedSelectedFiles = cloneDeep(selectedFiles);

    // Get the file by uploadTrackingId
    const selectedFile = modifiedSelectedFiles.find((vaultFile: IVaultFile) => vaultFile.uploadTrackingId === uploadTrackingId);

    // If found modify the status (& reset progress) for this entry and update the selected files list
    if (selectedFile) {
      selectedFile.uploadStatus = uploadStatus;
      selectedFile.progressPercentage = null;

      dispatch({
        type: ActionType.UpdateFiles,
        payload: modifiedSelectedFiles,
      });
    }
  }
};

// This updates the progress for the file during upload (progress is normally calculated per stage (e.g. pre-processing, uploading etc)
export const updateFileProgress = (uploadTrackingId: string, progressPercentage: string) => async (dispatch: Dispatch<any>, getState: () => IAppState) => {
  const { selectedFiles } = getState().vaultUploadDialog;
  if (uploadTrackingId && selectedFiles && selectedFiles.length > 0) {
    const modifiedSelectedFiles = cloneDeep(selectedFiles);

    // Get the file by uploadTrackingId
    const selectedFile = modifiedSelectedFiles.find((vaultFile: IVaultFile) => vaultFile.uploadTrackingId === uploadTrackingId);

    // If found modify the progress for this entry and update the selected files list
    if (selectedFile) {
      selectedFile.progressPercentage = progressPercentage;

      dispatch({
        type: ActionType.UpdateFiles,
        payload: modifiedSelectedFiles,
      });
    }
  }
};
